import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Table, Input, Button, Space, Row, Col,
    Breadcrumb, message, Typography, Tooltip, Tag, Select, Popconfirm
} from "antd";
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import {
    PlusOutlined, HomeOutlined, SnippetsOutlined,
    SearchOutlined, CheckCircleOutlined, ClockCircleOutlined, DownloadOutlined
} from "@ant-design/icons";
import api from "../../services/axiosInstance";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;

const ManageMaintenance = () => {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [maintainCats, setMaintainCats] = useState([]);
    const [filters, setFilters] = useState({ type: 'all', status: '', mc_id: 0 });
    const [search, setSearch] = useState("");

    const fetchCategories = async () => {
        try {
            const res = await api.get('/masterdropdown/listof_activemaintaincategories.php');
            setMaintainCats(res.data.dataset);
        } catch {
            message.error("Failed to load maintenance categories.");
        }
    };

    const fetchTasks = useCallback(async () => {
        try {
            setLoading(true);
            const { type, status, mc_id } = filters;
            const res = await api.get(`/maintenance/list.php?type=${type}&status=${status}&mc_id=${mc_id}`);
            setTasks(res.data.dataset);
        } catch {
            message.error("Failed to fetch maintenance tasks.");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchTasks();
        fetchCategories();
    }, [fetchTasks]);

    // Client-side search over the loaded tasks
    const filteredTasks = search.trim()
        ? tasks.filter((t) => {
            const q = search.trim().toLowerCase();
            return (
                (t.main_title || "").toLowerCase().includes(q) ||
                (t.mc_name || "").toLowerCase().includes(q) ||
                (t.type || "").toLowerCase().includes(q)
            );
        })
        : tasks;

    const handleComplete = async (record) => {
        try {
            const res = await api.post('/maintenance/complete.php', {
                type: record.type.toLowerCase(),
                main_id: record.main_id
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                fetchTasks();
            }
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to complete task.");
        }
    };

    const getStatusTag = (status) => {
        if (status === 'done') return <Tag color="green">DONE</Tag>;
        if (status === 'pending') return <Tag color="orange">PENDING</Tag>;
        return <Tag>{status.toUpperCase()}</Tag>;
    };

    const handleExport = async () => {
        try {
            const res = await api.get('/reports/export_maintenance.php', {
                params: { type: filters.type === 'all' ? 'land' : filters.type },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `maintenance_report_${dayjs().format('YYYYMMDD')}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to download export.");
        }
    };

    const columns = [
        {
            title: 'Target Date', dataIndex: 'target_date', key: 'target_date', width: 120,
            render: (d) => dayjs(d).format('YYYY-MM-DD')
        },
        {
            title: 'Level', dataIndex: 'type', key: 'type', width: 100,
            render: (t) => <Tag color="blue">{t}</Tag>
        },
        {
            title: 'Category', dataIndex: 'mc_name', key: 'mc_name',
        },
        {
            title: 'Title', dataIndex: 'main_title', key: 'main_title',
        },
        {
            title: 'Status', dataIndex: 'main_status', key: 'main_status', width: 100,
            render: (s) => getStatusTag(s)
        },
        {
            title: 'Completed', dataIndex: 'complete_date', key: 'complete_date', width: 120,
            render: (d) => d ? dayjs(d).format('YYYY-MM-DD') : '-'
        },
        {
            title: 'Action', key: 'action', align: 'center', width: 100,
            render: (_, record) => record.main_status === 'pending' ? (
                <Popconfirm title="Mark as completed?" onConfirm={() => handleComplete(record)}>
                    <Button type="text" icon={<CheckCircleOutlined style={{ color: 'green' }} />} />
                </Popconfirm>
            ) : <CheckCircleOutlined style={{ color: '#ccc' }} />
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                                                    <Breadcrumb
                                className="fluid-breadcrumb"
                                items={[
                                    { href: '/dash', title: <HomeOutlined /> },
                                    { title: 'Maintenance Tasks' },
                                ]}
                            />
                        <h1 className="fluid-page-title">
                            <SnippetsOutlined /> Maintenance Tasks
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Monitor and manage agricultural maintenance activities</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card" style={{ marginBottom: 20 }}>
                    <Row gutter={16} align="middle">
                        <Col xs={24} md={5}>
                            <Select allowClear style={{ width: '100%' }} value={filters.type} onChange={(val) => setFilters({ ...filters, type: val })}>
                                <Option value="all">All Levels</Option>
                                <Option value="land">Land</Option>
                                <Option value="section">Section</Option>
                                <Option value="field">Field</Option>
                                <Option value="plant">Plant</Option>
                            </Select>
                        </Col>
                        <Col xs={24} md={5}>
                            <Select allowClear style={{ width: '100%' }} value={filters.status} onChange={(val) => setFilters({ ...filters, status: val })} placeholder="All Status">
                                <Option value="">All Status</Option>
                                <Option value="pending">Pending</Option>
                                <Option value="done">Done</Option>
                            </Select>
                        </Col>
                        <Col xs={24} md={5}>
                            <Select allowClear style={{ width: '100%' }} value={filters.mc_id} onChange={(val) => setFilters({ ...filters, mc_id: val })} placeholder="All Categories">
                                <Option value={0}>All Categories</Option>
                                {maintainCats.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                            </Select>
                        </Col>
                        <Col xs={24} md={5}>
                            <Input
                                placeholder="Search title, category..."
                                prefix={<SearchOutlined />}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                allowClear
                            />
                        </Col>
                        <Col xs={24} md={4} style={{ textAlign: 'right' }}>
                            <Space>
                                <Button 
                                    icon={<DownloadOutlined />} 
                                    onClick={handleExport}
                                >
                                    Export
                                </Button>
                                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/maintenance/add')}>
                                    New Task
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </div>

                <div className="fluid-card">
                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={filteredTasks}
                            rowKey={(r) => `${r.type}-${r.main_id}`}
                            loading={loading}
                            scroll={{ x: 800 }}
                            pagination={{ pageSize: 10 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />
        </Layout>
    );
};

export default ManageMaintenance;
